import React, { useState, useEffect, useCallback, useMemo } from "react";
import { JsonRpc } from "@proton/js";
import Loader from "./loader/Loader";
import FilterDropdown from "./FilterDropdown";
import ImageUpload from "./ImageUpload";
import { editMarketTransaction } from "../utils/crypto";
import { normalizeTimestamp } from "../utils/dateUtils";
import { fetchOutcomeNamesForMarket } from "../utils/outcomes";

export interface EditableMarket {
  id: number;
  question: string;
  category: string;
  expireTime: number;
  outcomes: string[];
  image_url: string;
  resolved: boolean;
  suggested_by: string;
}

interface SuperAdminEditMarketSectionProps {
  session: any;
  showToast: (message: string, type?: "success" | "error") => void;
}

const QUESTION_PREVIEW_LEN = 50;

const EDIT_MARKET_CATEGORY_OPTIONS: ReadonlyArray<{
  value: string;
  label: string;
}> = [
  { value: "crypto", label: "Crypto" },
  { value: "politics", label: "Politics" },
  { value: "sports", label: "Sports" },
  { value: "technology", label: "Technology" },
  { value: "finance", label: "Finance" },
  { value: "other", label: "Other" },
];

function formatMarketOptionLabel(market: EditableMarket): string {
  const q =
    market.question.length > QUESTION_PREVIEW_LEN
      ? `${market.question.slice(0, QUESTION_PREVIEW_LEN)}...`
      : market.question;
  const by = market.suggested_by ? ` (by ${market.suggested_by})` : "";
  return `#${market.id}: ${q}${by}`;
}

const SuperAdminEditMarketSection: React.FC<
  SuperAdminEditMarketSectionProps
> = ({ session, showToast }) => {
  const [allMarkets, setAllMarkets] = useState<EditableMarket[]>([]);
  const [loadingMarkets, setLoadingMarkets] = useState(false);
  const [editMarketId, setEditMarketId] = useState("");
  const [editQuestion, setEditQuestion] = useState("");
  const [editCategory, setEditCategory] = useState("");
  const [editImageUrl, setEditImageUrl] = useState("");
  const [editDescription, setEditDescription] = useState("");
  const [editResolutionCriteria, setEditResolutionCriteria] = useState("");
  const [editImageFile, setEditImageFile] = useState<File | null>(null);
  const [editImagePreview, setEditImagePreview] = useState("");
  const [editLoading, setEditLoading] = useState(false);
  const [editUploadingImage, setEditUploadingImage] = useState(false);
  const selectedEditMarket = editMarketId
    ? allMarkets.find((market) => market.id === parseInt(editMarketId, 10))
    : null;
  const isEditQuestionChanged =
    selectedEditMarket != null &&
    editQuestion.trim() !== selectedEditMarket.question.trim();
  const isEditSubmitDisabled =
    editLoading || editUploadingImage || !isEditQuestionChanged;

  const fetchAllMarkets = useCallback(async () => {
    if (!session) return;

    setLoadingMarkets(true);
    try {
      const rpc = new JsonRpc(process.env.REACT_APP_RPC_ENDPOINT);
      const contractName = process.env.REACT_APP_CONTRACT_NAME || "xpredict";

      const result = await rpc.get_table_rows({
        json: true,
        code: contractName,
        scope: contractName,
        table: "markets3",
        limit: 1000,
      });

      const marketRows = result.rows.filter((row: any) => {
        const isLive = row.isLive === true || row.isLive === 1;
        const status = Number(row.status ?? 0);
        return !row.resolved && !isLive && status !== 100;
      });

      const markets = await Promise.all(
        marketRows.map(async (row: any) => ({
          id: row.id,
          question: row.question,
          category: (row.category || "").trim().toLowerCase(),
          expireTime: normalizeTimestamp(row.expire),
          outcomes: await fetchOutcomeNamesForMarket(rpc, contractName, row.id),
          image_url: row.image_url || "",
          resolved: row.resolved || false,
          suggested_by: row.suggested_by || "",
        })),
      );

      setAllMarkets(markets);
    } catch (error) {
      console.error("Error fetching markets:", error);
    } finally {
      setLoadingMarkets(false);
    }
  }, [session]);

  useEffect(() => {
    if (session) {
      fetchAllMarkets();
    }
  }, [session, fetchAllMarkets]);

  const marketPickerOptions = useMemo(() => {
    const marketOpts = allMarkets.map((market) => ({
      value: String(market.id),
      label: formatMarketOptionLabel(market),
    }));
    if (!editMarketId) {
      return [{ value: "", label: "-- Select a market --" }, ...marketOpts];
    }
    return marketOpts;
  }, [allMarkets, editMarketId]);

  const categoryPickerOptions = useMemo(() => {
    const base = EDIT_MARKET_CATEGORY_OPTIONS.map((o) => ({
      value: o.value,
      label: o.label,
    }));
    const knownValues = new Set(base.map((o) => o.value));
    let cats = base;
    if (editCategory && !knownValues.has(editCategory)) {
      cats = [
        {
          value: editCategory,
          label: editCategory.charAt(0).toUpperCase() + editCategory.slice(1),
        },
        ...base,
      ];
    }
    if (!editCategory) {
      return [{ value: "", label: "Select category" }, ...cats];
    }
    return cats;
  }, [editCategory]);

  const handleEditMarketSelect = async (marketId: string) => {
    setEditMarketId(marketId);
    if (marketId) {
      const market = allMarkets.find((m) => m.id === parseInt(marketId, 10));
      if (market) {
        setEditQuestion(market.question);
        const rawCat = (market.category || "").trim().toLowerCase();
        const matchedCat = EDIT_MARKET_CATEGORY_OPTIONS.find(
          (o) => o.value === rawCat,
        );
        setEditCategory(matchedCat ? matchedCat.value : rawCat);
        setEditImageUrl(market.image_url);
        setEditImagePreview("");
        setEditImageFile(null);

        try {
          const response = await fetch(
            `/api/market_meta.php?market_id=${marketId}`,
          );
          if (response.ok) {
            const data = await response.json();
            if (data.success) {
              setEditDescription(data.description || "");
              setEditResolutionCriteria(data.resolution_criteria || "");
            } else {
              setEditDescription("");
              setEditResolutionCriteria("");
            }
          } else {
            setEditDescription("");
            setEditResolutionCriteria("");
          }
        } catch (error) {
          console.error("Error fetching market meta:", error);
          setEditDescription("");
          setEditResolutionCriteria("");
        }
      }
    } else {
      setEditQuestion("");
      setEditCategory("");
      setEditImageUrl("");
      setEditDescription("");
      setEditResolutionCriteria("");
      setEditImagePreview("");
      setEditImageFile(null);
    }
  };

  const handleEditImageFileSelect = (file: File) => {
    if (!["image/jpeg", "image/png", "image/webp"].includes(file.type)) {
      showToast("Only JPG, PNG, and WebP images are allowed", "error");
      return;
    }

    if (file.size > 2 * 1024 * 1024) {
      showToast("Image must be less than 2MB", "error");
      return;
    }

    setEditImageFile(file);
    setEditImagePreview(URL.createObjectURL(file));
  };

  const handleEditUploadImage = async () => {
    if (!editImageFile) return;

    setEditUploadingImage(true);
    try {
      const formData = new FormData();
      formData.append("file", editImageFile);

      const response = await fetch("/api/upload.php", {
        method: "POST",
        body: formData,
      });

      if (!response.ok) {
        throw new Error("Upload failed");
      }

      const data = await response.json();
      setEditImageUrl(data.url);
      showToast("Image uploaded successfully!");
    } catch (error) {
      console.error("Error uploading image:", error);
      showToast("Failed to upload image: " + error, "error");
    } finally {
      setEditUploadingImage(false);
    }
  };

  const handleEditClearImage = () => {
    setEditImageFile(null);
    setEditImagePreview("");
    setEditImageUrl("");
  };

  const handleEditMarket = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!session || !editMarketId || !editQuestion || !editCategory) {
      showToast("Please fill in all required fields", "error");
      return;
    }
    if (!isEditQuestionChanged) {
      return;
    }

    const selectedMarket = allMarkets.find(
      (market) => market.id === parseInt(editMarketId, 10),
    );
    if (!selectedMarket) {
      showToast("Selected market could not be loaded", "error");
      return;
    }

    setEditLoading(true);
    try {
      const payload: Parameters<typeof editMarketTransaction>[1] = {
        market_id: selectedMarket.id,
        expireTime: selectedMarket.expireTime,
        outcomes: selectedMarket.outcomes,
      };

      if (editQuestion !== selectedMarket.question) {
        payload.question = editQuestion;
      }
      payload.category =
        editCategory !== selectedMarket.category ? editCategory : "";
      payload.image_url =
        editImageUrl !== selectedMarket.image_url ? editImageUrl : "";

      await editMarketTransaction(session, payload);

      if (editDescription || editResolutionCriteria) {
        try {
          await fetch("/api/market_meta.php", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              market_id: parseInt(editMarketId, 10),
              description: editDescription,
              resolution_criteria: editResolutionCriteria,
            }),
          });
        } catch (metaError) {
          console.error("Error saving market meta:", metaError);
        }
      }

      showToast("Market updated successfully!");
      fetchAllMarkets();
      setEditMarketId("");
      setEditQuestion("");
      setEditCategory("");
      setEditDescription("");
      setEditResolutionCriteria("");
      setEditImageUrl("");
      setEditImageFile(null);
      setEditImagePreview("");
    } catch (error) {
      console.error("Error updating market:", error);
      showToast("Failed to update market: " + error, "error");
    } finally {
      setEditLoading(false);
    }
  };

  return (
    <div className="admin-section">
      <div className="superadmin-edit-header">
        <h3>Edit Any Market</h3>
        {selectedEditMarket && (
          <span className="superadmin-edit-pending-badge">
            Pending Approval
          </span>
        )}
      </div>
      <p className="section-description">
        As a SuperAdmin, you can edit any market regardless of who created it.
      </p>

      <form onSubmit={handleEditMarket} className="admin-form">
        <div className="form-group">
          <label>Select Market to Edit</label>
          {loadingMarkets ? (
            <Loader label="Loading markets..." className="app-loader-compact" />
          ) : (
            <FilterDropdown
              value={editMarketId}
              options={marketPickerOptions}
              onChange={(id) => {
                void handleEditMarketSelect(id);
              }}
              className="superadmin-edit-market-picker"
              ariaLabel="Select market to edit"
            />
          )}
        </div>

        {editMarketId && (
          <>
            <div className="form-group">
              <label>Question</label>
              <input
                type="text"
                value={editQuestion}
                onChange={(e) => setEditQuestion(e.target.value)}
                required
              />
            </div>

            <div className="form-group">
              <label>Category</label>
              <FilterDropdown
                value={editCategory}
                options={categoryPickerOptions}
                onChange={setEditCategory}
                className="superadmin-edit-category-picker"
                ariaLabel="Market category"
              />
            </div>

            <div className="form-group">
              <label>Description</label>
              <textarea
                value={editDescription}
                onChange={(e) => setEditDescription(e.target.value)}
                placeholder="Provide additional context about this market..."
                rows={3}
                style={{
                  backgroundColor: "#1f2937",
                  color: "#e5e7eb",
                  border: "1px solid #374151",
                  borderRadius: "6px",
                  padding: "10px",
                  width: "100%",
                  resize: "vertical",
                }}
              />
            </div>

            <div className="form-group">
              <label>Resolution Criteria</label>
              <textarea
                value={editResolutionCriteria}
                onChange={(e) => setEditResolutionCriteria(e.target.value)}
                placeholder="How will this market be resolved? What sources will be used?"
                rows={3}
                style={{
                  backgroundColor: "#1f2937",
                  color: "#e5e7eb",
                  border: "1px solid #374151",
                  borderRadius: "6px",
                  padding: "10px",
                  width: "100%",
                  resize: "vertical",
                }}
              />
            </div>

            <div className="form-group">
              <label>Market Image (Optional)</label>
              <ImageUpload
                imageUrl={editImageUrl}
                imagePreview={editImagePreview}
                imageFile={editImageFile}
                uploading={editUploadingImage}
                onImageUrlChange={setEditImageUrl}
                onFileSelect={handleEditImageFileSelect}
                onUpload={handleEditUploadImage}
                onClear={handleEditClearImage}
              />
            </div>

            <div
              title={
                !editLoading && !editUploadingImage && !isEditQuestionChanged
                  ? "no changes made yet."
                  : undefined
              }
              style={{ width: "100%" }}
            >
              <button
                type="submit"
                disabled={isEditSubmitDisabled}
                className="submit-button"
              >
                {editLoading ? "Updating..." : "Update Market"}
              </button>
            </div>
          </>
        )}
      </form>
    </div>
  );
};

export default SuperAdminEditMarketSection;
